import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Clock, Repeat, ArrowRight, AlertTriangle, Check, X, Tag, Sparkles, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import HouseRulesDialog from '../components/HouseRulesDialog';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = Array.from({ length: 33 }, (_, i) => {
  const totalMin = 6 * 60 + i * 30; // 06:00 -> 22:00 in 30-min steps
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});

// Build a 6-month grid of dates starting from today
function build6MonthGrid() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const months = [];
  for (let m = 0; m < 6; m++) {
    const d = new Date(today.getFullYear(), today.getMonth() + m, 1);
    months.push(d);
  }
  return months;
}

function fmtIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayOfWeekMonFirst(d) {
  // JS: Sun=0..Sat=6 -> we want Mon=0..Sun=6
  return (d.getDay() + 6) % 7;
}

export default function RecurringBookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const daysFull = t('booking.daysFull', { returnObjects: true });
  const daysShort = t('booking.daysShort', { returnObjects: true });
  const { user, token, loading } = useAuth();
  const [spaces, setSpaces] = useState([]);
  const [spaceId, setSpaceId] = useState('');
  const [mode, setMode] = useState('hourly'); // 'hourly' | 'half_day' | 'daily' | 'weekly'
  const [halfDayPeriod, setHalfDayPeriod] = useState('am'); // 'am' | 'pm'
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('12:00');
  const [selectedDates, setSelectedDates] = useState([]); // ISO strings
  const [conflicts, setConflicts] = useState([]); // ISO strings
  const [submitting, setSubmitting] = useState(false);
  const [invoiceType, setInvoiceType] = useState('private');

  const isAdmin = user?.role === 'admin' || user?.email === 'dequenne@hotmail.com';

  // Legal billing profile complete? Required for the "business invoice" opt-in.
  // Accepts pro_vat (with company + VAT + address) OR pro_non_vat (with
  // company + BCE + address). Private customers cannot request a B2B invoice.
  const billingComplete = React.useMemo(() => {
    if (!user) return false;
    const type = user.customer_type || 'private';
    const hasCompany = Boolean((user.company || '').trim());
    const hasAddress = Boolean((user.address || '').trim());
    if (type === 'pro_vat') {
      return hasCompany && hasAddress && Boolean((user.company_vat || '').trim());
    }
    if (type === 'pro_non_vat') {
      return hasCompany && hasAddress && Boolean((user.company_bce || '').trim());
    }
    return false;
  }, [user]);

  // Subscription discount (% off) — comes from /auth/me; refreshed once on mount
  const [discountPct, setDiscountPct] = useState(0);
  // Voucher
  const [voucherInput, setVoucherInput] = useState('');
  const [voucher, setVoucher] = useState(null); // {code, discount_type, discount_value}
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  // Hold-open mode (conferences) — applies to all sessions in the series
  const [doorHoldOpen, setDoorHoldOpen] = useState(false);
  const [houseRulesOpen, setHouseRulesOpen] = useState(false);
  const [pendingProvider, setPendingProvider] = useState(null);
  const [paymentDisclaimer, setPaymentDisclaimer] = useState('');
  const consentRef = React.useRef(null);

  useEffect(() => {
    axios.get(`${API}/site-settings`)
      .then((r) => setPaymentDisclaimer(r.data?.payment_disclaimer || ''))
      .catch(() => {});
  }, []);

  const requestPayment = (provider) => {
    if (availableDates.length === 0) { toast.error(t('booking.selectAtLeastOne')); return; }
    if (hours <= 0) { toast.error(t('booking.endAfterStart')); return; }
    setPendingProvider(provider);
    setHouseRulesOpen(true);
  };

  const onHouseRulesConfirm = () => {
    const shownText = (paymentDisclaimer && String(paymentDisclaimer).trim())
      || "En confirmant le paiement, je m'engage à : rendre les locaux dans l'état initial, ranger meubles/chaises/matériel, laisser toilettes propres, sortir mes déchets. Supplément forfaitaire 30 EUR (nettoyage et remise en état) en cas de non-respect.";
    consentRef.current = { accepted_at: new Date().toISOString(), text: shownText };
    setHouseRulesOpen(false);
    const provider = pendingProvider;
    setPendingProvider(null);
    handleSubmit(provider || 'stripe');
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/auth'); return; }
    axios.get(`${API}/spaces`).then(res => {
      setSpaces(res.data || []);
      if (res.data?.length) {
        // Honor ?space=xxx from the URL (comes from "Les Espaces" → "Réserver
        // plusieurs dates"), fall back to the first space in the list.
        const wanted = searchParams.get('space');
        const match = wanted && res.data.find(s => s.space_id === wanted);
        setSpaceId(match ? match.space_id : res.data[0].space_id);
      }
    }).catch(() => {});
    setDiscountPct(Number(user?.discount_percent || 0));
  }, [user, loading, navigate, token, searchParams]);

  const space = useMemo(() => spaces.find(s => s.space_id === spaceId), [spaces, spaceId]);

  // Auto-set start/end when the booking mode changes (except for hourly where the user picks)
  useEffect(() => {
    if (mode === 'hourly') return;
    if (mode === 'half_day') {
      if (halfDayPeriod === 'am') { setStartTime('06:00'); setEndTime('13:00'); }
      else { setStartTime('14:00'); setEndTime('22:00'); }
    } else if (mode === 'daily' || mode === 'weekly') {
      setStartTime('06:00'); setEndTime('22:00');
    }
  }, [mode, halfDayPeriod]);

  // Reset selection when space or time changes
  useEffect(() => { setSelectedDates([]); setConflicts([]); }, [spaceId, startTime, endTime, mode]);

  // Lock day-of-week from the first selected date — ignored in weekly mode where
  // every click already expands to a whole Monday→Sunday week.
  const lockedDow = mode === 'weekly'
    ? null
    : (selectedDates.length ? dayOfWeekMonFirst(new Date(selectedDates[0] + 'T00:00:00')) : null);

  const months = useMemo(build6MonthGrid, []);

  const toggleDate = (iso, dDow) => {
    // Weekly mode: clicking any day (in)selects the entire Monday→Sunday week
    if (mode === 'weekly') {
      const d = new Date(iso + 'T00:00:00');
      const monday = new Date(d);
      monday.setDate(d.getDate() - dDow); // dDow is Mon=0..Sun=6
      const weekIsos = [];
      for (let i = 0; i < 7; i++) {
        const wd = new Date(monday);
        wd.setDate(monday.getDate() + i);
        weekIsos.push(fmtIso(wd));
      }
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const futureWeekIsos = weekIsos.filter(w => new Date(w + 'T00:00:00') >= today);
      const alreadySelected = futureWeekIsos.every(w => selectedDates.includes(w));
      setSelectedDates(prev => {
        if (alreadySelected) return prev.filter(x => !futureWeekIsos.includes(x));
        const merged = Array.from(new Set([...prev, ...futureWeekIsos])).sort();
        return merged;
      });
      return;
    }
    if (lockedDow !== null && dDow !== lockedDow) {
      toast.warning(t('booking.sameDayWarning', { day: daysFull[lockedDow] }));
      return;
    }
    setSelectedDates(prev => prev.includes(iso) ? prev.filter(d => d !== iso) : [...prev, iso].sort());
  };

  // Live conflict check — debounced
  useEffect(() => {
    if (!spaceId || selectedDates.length === 0) { setConflicts([]); return; }
    if (startTime >= endTime) { setConflicts([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/bookings/recurring/check-availability`, {
          space_id: spaceId,
          dates: selectedDates,
          start_time: startTime,
          end_time: endTime,
        }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
        setConflicts(res.data.conflicts || []);
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [spaceId, selectedDates, startTime, endTime, token]);

  const availableDates = selectedDates.filter(d => !conflicts.includes(d));
  const toH = (t) => { const [h, m] = t.split(':').map(Number); return h + (m || 0) / 60; };
  const hours = Math.max(toH(endTime) - toH(startTime), 0);
  const sessionsCount = availableDates.length;

  // Weekly mode: 7 days are selected per week but the customer is billed one
  // flat weekly rate per week. We count the unique Mondays amongst the
  // selected dates to know how many weeks to charge.
  const uniqueWeekMondays = React.useMemo(() => {
    if (mode !== 'weekly') return [];
    const mondays = new Set();
    for (const iso of availableDates) {
      const d = new Date(iso + 'T00:00:00');
      const dow = (d.getDay() + 6) % 7; // Mon=0..Sun=6
      const mon = new Date(d);
      mon.setDate(d.getDate() - dow);
      mondays.add(fmtIso(mon));
    }
    return Array.from(mondays).sort();
  }, [availableDates, mode]);
  const weeksCount = uniqueWeekMondays.length;
  // How many "pricing units" we are charging for (weeks in weekly mode, days
  // otherwise). This matches what the backend will compute.
  const pricingUnits = mode === 'weekly' ? weeksCount : sessionsCount;

  // Mirror backend pricing exactly: prices in DB are HT, customer pays TVAC (HT × 1.21).
  const VAT_MULT = 1.21;
  const baseHt = (() => {
    if (!space) return 0;
    if (mode === 'hourly')   return space.price_hourly * Math.max(hours, 0.5);
    if (mode === 'half_day') {
      const half = Number(space.price_half_day || 0);
      if (half <= 0) return space.price_hourly * Math.max(hours, 0.5);
      return hours <= 4 ? half : half + (hours - 4) * space.price_hourly;
    }
    if (mode === 'daily')    return Number(space.price_daily || 0);
    if (mode === 'weekly')   return Number(space.price_weekly || 0);
    return 0;
  })();
  const baseUnitPrice = Math.round(baseHt * VAT_MULT * 100) / 100;
  const discountedUnitPrice = discountPct > 0
    ? Math.round(baseUnitPrice * (1 - discountPct / 100) * 100) / 100
    : baseUnitPrice;
  const subtotalBeforeDiscount = Math.round(baseUnitPrice * pricingUnits * 100) / 100;
  const subscriptionDiscountAmount = Math.round((baseUnitPrice - discountedUnitPrice) * pricingUnits * 100) / 100;
  const subtotal = Math.round(discountedUnitPrice * pricingUnits * 100) / 100;

  // Free hours have been DEPRECATED on subscriptions — keep zeros so the rest of the JSX still compiles.
  const totalFreeHours = 0;
  const freeHoursDiscount = 0;
  const afterFree = subtotal;

  // Voucher (applied to subtotal-after-free)
  let voucherDiscountAmt = 0;
  if (voucher) {
    if (voucher.discount_type === 'percent') {
      voucherDiscountAmt = Math.round(afterFree * Number(voucher.discount_value) / 100 * 100) / 100;
    } else {
      voucherDiscountAmt = Math.round(Math.min(Number(voucher.discount_value), afterFree) * 100) / 100;
    }
  }
  const total = Math.max(0, Math.round((afterFree - voucherDiscountAmt) * 100) / 100);

  // Legacy aliases used elsewhere in the JSX
  const unitPrice = discountedUnitPrice;

  const applyVoucher = useCallback(async () => {
    const code = (voucherInput || '').trim().toUpperCase();
    if (!code) { toast.error(t('voucher.required', { defaultValue: 'Saisissez un code' })); return; }
    setValidatingVoucher(true);
    try {
      const res = await axios.post(`${API}/vouchers/validate`, { code }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
      });
      setVoucher(res.data || null);
      toast.success(t('voucher.applied', { defaultValue: 'Code promo appliqué ✓' }));
    } catch (e) {
      setVoucher(null);
      toast.error(e?.response?.data?.detail || t('voucher.invalid', { defaultValue: 'Code invalide ou expiré' }));
    } finally {
      setValidatingVoucher(false);
    }
  }, [voucherInput, token, t]);

  const removeVoucher = () => { setVoucher(null); setVoucherInput(''); };

  const handleAdminFree = async () => {
    if (!isAdmin) return;
    if (availableDates.length === 0) { toast.error(t('booking.selectAtLeastOne')); return; }
    if (hours <= 0) { toast.error(t('booking.endAfterStart')); return; }
    const ok = window.confirm(
      `Confirmer cette série de ${availableDates.length} session(s) GRATUITEMENT (test admin) ? Aucune facture ne sera générée. Un email avec un code d'accès unique sera envoyé.`
    );
    if (!ok) return;
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const bookingType = mode;
      const datesPayload = mode === 'weekly' ? uniqueWeekMondays : availableDates;
      const create = await axios.post(`${API}/bookings/recurring`, {
        space_id: spaceId,
        dates: datesPayload,
        start_time: startTime,
        end_time: endTime,
        booking_type: bookingType,
        voucher_code: null,
        use_free_hours_per_session: 0,
        door_hold_open: doorHoldOpen,
        invoice_type: 'private',
      }, { headers, withCredentials: true });
      const recurringId = create.data.recurring_id;
      const freeRes = await axios.post(`${API}/payments/admin-free-recurring`, {
        recurring_id: recurringId,
      }, { headers, withCredentials: true });
      if (freeRes.data?.free) {
        toast.success(`Série gratuite confirmée (${freeRes.data.sessions_count} sessions) — Code : ${freeRes.data.access_code || '—'}`);
        navigate('/dashboard');
      } else {
        toast.error('Confirmation gratuite échouée');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t('booking.createBookingError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (provider = 'stripe') => {
    if (availableDates.length === 0) { toast.error(t('booking.selectAtLeastOne')); return; }
    if (hours <= 0) { toast.error(t('booking.endAfterStart')); return; }
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Map UI mode to backend booking_type and the dates list it expects:
      // weekly mode sends ONE Monday per week with booking_type="weekly" so
      // the backend charges price_weekly × weeks (not hourly × 16 × 7 × weeks).
      const bookingType = mode;
      const datesPayload = mode === 'weekly' ? uniqueWeekMondays : availableDates;
      const create = await axios.post(`${API}/bookings/recurring`, {
        space_id: spaceId,
        dates: datesPayload,
        start_time: startTime,
        end_time: endTime,
        booking_type: bookingType,
        voucher_code: voucher?.code || null,
        use_free_hours_per_session: 0,
        door_hold_open: doorHoldOpen,
        disclaimer_accepted_at: consentRef.current?.accepted_at || null,
        disclaimer_accepted_text: consentRef.current?.text || null,
        invoice_type: (invoiceType === 'business' && billingComplete) ? 'business' : 'private',
      }, { headers, withCredentials: true });
      const recurringId = create.data.recurring_id;
      if (provider === 'paypal') {
        const pp = await axios.post(`${API}/payments/paypal/create-order`, {
          recurring_id: recurringId,
          origin_url: window.location.origin,
        }, { headers, withCredentials: true });
        if (pp.data?.approval_url) window.location.href = pp.data.approval_url;
        else toast.error('PayPal: ' + t('booking.createBookingError'));
      } else {
        const checkout = await axios.post(`${API}/payments/checkout-recurring`, {
          recurring_id: recurringId,
          origin_url: window.location.origin,
        }, { headers, withCredentials: true });
        window.location.href = checkout.data.url;
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const networkErr = !err.response;
      console.error('[Recurring] Error', { status, detail, err });
      let msg;
      if (networkErr) msg = 'Serveur indisponible. Réessayez dans quelques secondes.';
      else if (detail) msg = detail;
      else msg = `Erreur ${status || ''} - ${t('booking.createBookingError')}`;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] font-body" data-testid="recurring-booking-page">
      <SEO titleKey="seo.recurring.title" descriptionKey="seo.recurring.description" />
      {/* Hero */}
      <section className="bg-[#1A3C34] py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C77D63]/20 border border-[#C77D63]/30 rounded-full text-sm font-semibold text-[#C77D63] mb-6">
            <Repeat className="w-4 h-4" /> {t('booking.recurringTitle')}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
            {t('booking.recurringHero1')}
            <br />
            <span className="text-[#C77D63]">{t('booking.recurringHero2')}</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/60 max-w-2xl">
            {t('booking.recurringIntro')}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">

          {/* Left column: setup + calendar */}
          <div className="space-y-6">
            {/* Setup */}
            <div className="bg-white border border-[#E2E2DF] rounded-xl p-5 sm:p-6">
              <h2 className="font-heading text-lg font-bold text-[#1A3C34] mb-4">1. {t('booking.configTitle')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-3">
                  <label className="text-xs font-semibold text-[#1A3C34]">{t('booking.spaceLabel')}</label>
                  <Select value={spaceId} onValueChange={setSpaceId}>
                    <SelectTrigger className="h-11 mt-1 border-[#E2E2DF]" data-testid="recurring-space-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {spaces.map(s => (
                        <SelectItem key={s.space_id} value={s.space_id}>{s.name || t(`spaces.names.${s.space_id}`, { defaultValue: s.space_id })} - {s.price_hourly} EUR/h</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Booking mode: hourly / half-day / daily / weekly */}
                <div className="sm:col-span-3">
                  <label className="text-xs font-semibold text-[#1A3C34]">
                    {t('booking.modeLabel', { defaultValue: 'Type de réservation' })}
                  </label>
                  <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="recurring-mode-tabs">
                    {[
                      { k: 'hourly',   label: t('booking.hourly', { defaultValue: 'À l\'heure' }) },
                      { k: 'half_day', label: t('booking.halfDay', { defaultValue: 'Demi-journée' }) },
                      { k: 'daily',    label: t('booking.dailyPreset', { defaultValue: 'Jour' }) },
                      { k: 'weekly',   label: t('booking.weekly', { defaultValue: 'Semaine' }) },
                    ].map(opt => (
                      <button
                        key={opt.k}
                        type="button"
                        onClick={() => setMode(opt.k)}
                        data-testid={`recurring-mode-${opt.k}`}
                        className={
                          'h-10 rounded-md text-sm font-medium border transition-colors ' +
                          (mode === opt.k
                            ? 'bg-[#1A3C34] text-white border-[#1A3C34]'
                            : 'bg-white text-[#1A3C34] border-[#E2E2DF] hover:border-[#C77D63]')
                        }
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Half-day AM/PM picker */}
                {mode === 'half_day' && (
                  <div className="sm:col-span-3">
                    <label className="text-xs font-semibold text-[#1A3C34]">
                      {t('booking.halfDayPeriodLabel', { defaultValue: 'Période' })}
                    </label>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      {[
                        { k: 'am', label: t('booking.morning', { defaultValue: 'Matin (06:00 → 13:00)' }) },
                        { k: 'pm', label: t('booking.afternoon', { defaultValue: 'Après-midi (14:00 → 22:00)' }) },
                      ].map(opt => (
                        <button
                          key={opt.k}
                          type="button"
                          onClick={() => setHalfDayPeriod(opt.k)}
                          data-testid={`recurring-halfday-${opt.k}`}
                          className={
                            'h-10 rounded-md text-sm font-medium border transition-colors ' +
                            (halfDayPeriod === opt.k
                              ? 'bg-[#C77D63] text-white border-[#C77D63]'
                              : 'bg-white text-[#1A3C34] border-[#E2E2DF] hover:border-[#C77D63]')
                          }
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hourly: free start/end pickers */}
                {mode === 'hourly' && (
                  <>
                    <div>
                      <label className="text-xs font-semibold text-[#1A3C34]">{t('booking.startTimeLabel')}</label>
                      <Select value={startTime} onValueChange={setStartTime}>
                        <SelectTrigger className="h-11 mt-1 border-[#E2E2DF]" data-testid="recurring-start-time"><SelectValue /></SelectTrigger>
                        <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#1A3C34]">{t('booking.endTimeLabel')}</label>
                      <Select value={endTime} onValueChange={setEndTime}>
                        <SelectTrigger className="h-11 mt-1 border-[#E2E2DF]" data-testid="recurring-end-time"><SelectValue /></SelectTrigger>
                        <SelectContent>{TIME_SLOTS.concat(['22:30', '23:00']).filter(t => t > startTime).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col justify-end">
                      <div className="text-xs text-[#737373]">{t('booking.durationLabel')}</div>
                      <div className="font-heading text-lg font-bold text-[#1A3C34]">{t('booking.hoursPerSession', { hours })}</div>
                    </div>
                  </>
                )}

                {/* Preset modes: show read-only schedule */}
                {mode !== 'hourly' && (
                  <div className="sm:col-span-3 flex items-center gap-3 bg-[#F9F9F7] border border-[#E2E2DF] rounded-md px-4 py-3">
                    <Clock className="w-4 h-4 text-[#1A3C34]" />
                    <div className="text-sm">
                      <span className="text-[#737373]">{t('booking.scheduleLabel', { defaultValue: 'Horaire' })} : </span>
                      <span className="font-bold text-[#1A3C34]">{startTime} — {endTime}</span>
                      <span className="ml-2 text-[#737373]">({t('booking.hoursPerSession', { hours })})</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Calendar */}
            <div className="bg-white border border-[#E2E2DF] rounded-xl p-5 sm:p-6">
              <h2 className="font-heading text-lg font-bold text-[#1A3C34] mb-1">2. {
                mode === 'weekly'
                  ? t('booking.selectWeeks', { defaultValue: 'Sélectionnez les semaines' })
                  : t('booking.selectDatesLabel', { defaultValue: 'Sélectionnez les dates' })
              }</h2>
              <p className="text-xs text-[#737373] mb-4">
                {mode === 'weekly'
                  ? t('booking.selectWeeksHintWeekly', { defaultValue: 'Cliquez sur n\'importe quel jour : la semaine complète (lundi → dimanche) est ajoutée. Reclic pour retirer la semaine.' })
                  : t('booking.selectWeeksHint')}
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                {months.map((m, idx) => (
                  <MonthGrid
                    key={idx}
                    month={m}
                    selectedDates={selectedDates}
                    conflicts={conflicts}
                    lockedDow={lockedDow}
                    onToggle={toggleDate}
                    locale={i18n.language}
                    daysShort={daysShort}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right column: summary */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="bg-white border border-[#E2E2DF] rounded-xl p-5 sm:p-6 space-y-4" data-testid="recurring-summary">
              <h2 className="font-heading text-lg font-bold text-[#1A3C34]">{t('booking.summary')}</h2>

              <Row label={t('booking.space')} value={space ? (space.name || t(`spaces.names.${space.space_id}`, { defaultValue: space.space_id })) : '—'} />
              {mode !== 'weekly' && (
                <Row label={t('booking.dayOfWeek')} value={lockedDow !== null ? daysFull[lockedDow] : '—'} />
              )}
              <Row label={t('booking.scheduleLabel')} value={`${startTime} - ${endTime}`} />
              <Row
                label={mode === 'weekly'
                  ? t('booking.weeksCount', { defaultValue: 'Semaines sélectionnées' })
                  : t('booking.selectedSessions')}
                value={String(mode === 'weekly' ? weeksCount : selectedDates.length)}
              />
              {conflicts.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2" data-testid="recurring-conflicts">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">{conflicts.length} {t('booking.conflictsTitle')}</p>
                    <p>{t('booking.conflictsHint')}</p>
                  </div>
                </div>
              )}
              <Row label={t('booking.billedSessions')} value={String(availableDates.length)} highlight />

              {/* HTVA notice — make it impossible to miss */}
              <div className="bg-[#C77D63]/10 border border-[#C77D63]/25 rounded-md px-3 py-1.5 flex items-center gap-1.5" data-testid="recurring-htva-notice">
                <span className="text-[#C77D63] text-xs">★</span>
                <span className="text-[11px] font-semibold text-[#C77D63]">
                  {t('booking.htvaNoticeProminent', { defaultValue: 'Tous les prix sont HTVA · TVA 21% ajoutée à l\'addition' })}
                </span>
              </div>

              {/* ---- Door hold-open mode (applies to all sessions) ---- */}
              <label
                className="flex items-start gap-3 cursor-pointer bg-[#1A3C34]/5 border border-[#1A3C34]/10 rounded-lg p-3 hover:bg-[#1A3C34]/10 transition-colors"
                data-testid="recurring-door-hold-open-block"
              >
                <input
                  type="checkbox"
                  checked={doorHoldOpen}
                  onChange={(e) => setDoorHoldOpen(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#1A3C34]"
                  data-testid="recurring-door-hold-open-checkbox"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm">🚪</span>
                    <span className="text-sm font-semibold text-[#1A3C34]">
                      {t('booking.doorHoldOpen', { defaultValue: 'Porte ouverte pendant la session' })}
                    </span>
                    <span className="text-[10px] bg-[#C77D63]/15 text-[#C77D63] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">
                      {t('booking.confMode', { defaultValue: 'mode conf' })}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#737373] mt-0.5 leading-snug">
                    {t('booking.doorHoldOpenHintRecurring', { defaultValue: "Idéal pour les conférences. S'applique à toutes les séances de la série. Vous pourrez basculer ouvert/fermé depuis votre tableau de bord pendant chaque séance." })}
                  </p>
                </div>
              </label>

              {/* ---- Voucher / promo code ---- */}
              <div className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-lg p-3" data-testid="recurring-voucher-block">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-[#C77D63]" />
                  <p className="text-xs font-semibold text-[#1A3C34]">
                    {t('booking.voucherTitle', { defaultValue: 'Code promo' })}
                  </p>
                </div>
                {voucher ? (
                  <div className="flex items-center justify-between gap-2 bg-white border border-emerald-200 rounded p-2" data-testid="recurring-voucher-applied">
                    <div className="text-xs text-emerald-900">
                      <span className="font-mono font-bold">{voucher.code}</span>
                      <span className="ml-2 text-[11px]">
                        {voucher.discount_type === 'percent'
                          ? `(-${voucher.discount_value}%)`
                          : `(-${Number(voucher.discount_value).toFixed(2)} EUR)`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={removeVoucher}
                      className="text-[11px] text-red-600 hover:underline"
                      data-testid="recurring-voucher-remove"
                    >
                      <X className="w-3 h-3 inline" /> {t('common.remove', { defaultValue: 'Retirer' })}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      value={voucherInput}
                      onChange={(e) => setVoucherInput(e.target.value)}
                      placeholder={t('booking.voucherPlaceholder', { defaultValue: 'CODE2026' })}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyVoucher(); } }}
                      className="h-9 flex-1 text-sm uppercase font-mono"
                      data-testid="recurring-voucher-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={applyVoucher}
                      disabled={validatingVoucher || !voucherInput.trim()}
                      data-testid="recurring-voucher-apply"
                      className="h-9 bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white text-xs"
                    >
                      {validatingVoucher ? '…' : t('booking.voucherApply', { defaultValue: 'Appliquer' })}
                    </Button>
                  </div>
                )}
              </div>

              <div className="border-t border-[#E2E2DF] pt-4 space-y-1">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-[#737373]">{t('booking.unitPriceTVAC', { defaultValue: 'Tarif unitaire TVAC' })}</span>
                  <span className="text-sm font-semibold text-[#1A3C34]">
                    {discountPct > 0 && (
                      <span className="line-through text-[#C2C2BD] mr-2 font-normal">{baseUnitPrice.toFixed(2)} EUR</span>
                    )}
                    {unitPrice.toFixed(2)} EUR
                  </span>
                </div>
                {sessionsCount > 0 && (
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-[#737373]">
                      {t('booking.subtotal', { defaultValue: 'Sous-total' })}
                    </span>
                    <span className="text-xs text-[#1A3C34]">{subtotalBeforeDiscount.toFixed(2)} EUR</span>
                  </div>
                )}
                {discountPct > 0 && sessionsCount > 0 && (
                  <div className="flex items-baseline justify-between text-emerald-700" data-testid="recurring-subscription-discount">
                    <span className="text-xs">
                      {t('booking.subscriptionDiscount', { defaultValue: 'Remise abonnement' })} (-{discountPct}%)
                    </span>
                    <span className="text-xs font-semibold">-{subscriptionDiscountAmount.toFixed(2)} EUR</span>
                  </div>
                )}
                {totalFreeHours > 0 && (
                  <div className="flex items-baseline justify-between text-emerald-700" data-testid="recurring-free-hours-discount">
                    <span className="text-xs">
                      {t('booking.freeHoursLine', { defaultValue: 'Heures gratuites' })} ({totalFreeHours}h)
                    </span>
                    <span className="text-xs font-semibold">-{freeHoursDiscount.toFixed(2)} EUR</span>
                  </div>
                )}
                {voucherDiscountAmt > 0 && (
                  <div className="flex items-baseline justify-between text-emerald-700" data-testid="recurring-voucher-discount">
                    <span className="text-xs">
                      {t('booking.voucherLine', { defaultValue: 'Code promo' })} ({voucher?.code})
                    </span>
                    <span className="text-xs font-semibold">-{voucherDiscountAmt.toFixed(2)} EUR</span>
                  </div>
                )}
                {/* HT / TVA / TVAC breakdown — total returned by backend is already TVAC */}
                {(() => {
                  const totalHT = Math.round(total / 1.21 * 100) / 100;
                  const tva = Math.round((total - totalHT) * 100) / 100;
                  return (
                    <>
                      <div className="flex items-baseline justify-between text-[#737373]">
                        <span className="text-xs">{t('booking.subtotalHT', { defaultValue: 'Sous-total HT' })}</span>
                        <span className="text-xs">{totalHT.toFixed(2)} EUR</span>
                      </div>
                      <div className="flex items-baseline justify-between text-[#737373]">
                        <span className="text-xs">{t('booking.vatLine', { defaultValue: 'TVA 21%' })}</span>
                        <span className="text-xs">+{tva.toFixed(2)} EUR</span>
                      </div>
                    </>
                  );
                })()}
                <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-[#E2E2DF]">
                  <span className="font-heading text-base font-bold text-[#1A3C34]">{t('booking.totalTVAC', { defaultValue: 'Total TVAC' })}</span>
                  <span className="font-heading text-2xl font-bold text-[#C77D63]" data-testid="recurring-total">{total.toFixed(2)} EUR</span>
                </div>
                {sessionsCount > 0 && subtotalBeforeDiscount - total > 0.01 && (
                  <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid="recurring-savings">
                    <Sparkles className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                    <span className="text-xs text-emerald-900">
                      {t('booking.savings', {
                        defaultValue: 'Vous économisez {{amount}} EUR ({{pct}}%) sur le tarif catalogue',
                        amount: (subtotalBeforeDiscount - total).toFixed(2),
                        pct: Math.round((subtotalBeforeDiscount - total) / subtotalBeforeDiscount * 100),
                      })}
                    </span>
                  </div>
                )}
              </div>

              <Button
                onClick={() => requestPayment('stripe')}
                disabled={submitting || availableDates.length === 0 || hours <= 0}
                className="w-full h-12 bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg font-bold"
                data-testid="recurring-submit"
              >
                {submitting ? t('booking.redirecting') : (
                  <>{t('booking.bookNow')} - {total.toFixed(2)} EUR <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <Button
                onClick={() => requestPayment('paypal')}
                disabled={submitting || availableDates.length === 0 || hours <= 0}
                className="w-full h-12 mt-2 bg-[#FFC439] hover:bg-[#FFB300] text-[#1A3C34] rounded-lg font-bold"
                data-testid="recurring-paypal-btn"
              >
                <span className="font-bold">Pay</span><span className="font-bold italic">Pal</span>
              </Button>
              {isAdmin && (
                <Button
                  onClick={handleAdminFree}
                  disabled={submitting || availableDates.length === 0 || hours <= 0}
                  className="w-full h-12 mt-2 bg-[#1A3C34] text-white hover:bg-[#163029] rounded-lg font-bold border border-dashed border-white/30"
                  data-testid="recurring-admin-free-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {t('booking.adminFreeCta', { defaultValue: 'Test admin — gratuit (sans facture)' })}
                </Button>
              )}
              <p className="text-[11px] text-[#737373] text-center">
                {t('booking.secureNote')}
              </p>
            </div>
          </aside>
        </div>
      </section>
      <HouseRulesDialog
        open={houseRulesOpen}
        message={paymentDisclaimer}
        isLoading={submitting}
        onClose={() => { setHouseRulesOpen(false); setPendingProvider(null); }}
        onConfirm={onHouseRulesConfirm}
        billingComplete={billingComplete}
        invoiceType={invoiceType}
        setInvoiceType={setInvoiceType}
      />
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#737373]">{label}</span>
      <span className={highlight ? 'font-bold text-[#1A3C34]' : 'text-[#1A3C34]'}>{value}</span>
    </div>
  );
}

function MonthGrid({ month, selectedDates, conflicts, lockedDow, onToggle, locale, daysShort }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const intlLocale = locale?.startsWith('nl') ? 'nl-BE' : locale?.startsWith('en') ? 'en-US' : 'fr-FR';
  const monthName = month.toLocaleDateString(intlLocale, { month: 'long', year: 'numeric' });
  const firstDow = dayOfWeekMonFirst(new Date(month.getFullYear(), month.getMonth(), 1));
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));

  return (
    <div>
      <h3 className="font-heading text-sm font-bold text-[#1A3C34] capitalize mb-2">{monthName}</h3>
      <div className="grid grid-cols-7 gap-1 text-[10px] font-semibold text-[#737373] mb-1">
        {(daysShort || ['L','M','M','J','V','S','D']).map((d, i) => (<div key={i} className="text-center">{d}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, idx) => {
          if (!d) return <div key={idx} />;
          const iso = fmtIso(d);
          const isPast = d < today;
          const dow = dayOfWeekMonFirst(d);
          const isSelected = selectedDates.includes(iso);
          const isConflict = isSelected && conflicts.includes(iso);
          const dimmed = lockedDow !== null && dow !== lockedDow;
          let cls = 'h-9 rounded-md text-xs font-medium transition-all flex items-center justify-center border ';
          if (isPast) cls += 'text-[#C2C2BD] bg-[#F0EFEA] border-transparent cursor-not-allowed ';
          else if (isConflict) cls += 'bg-red-50 text-red-600 border-red-200 line-through ';
          else if (isSelected) cls += 'bg-[#1A3C34] text-white border-[#1A3C34] hover:bg-[#163029] ';
          else if (dimmed) cls += 'text-[#C2C2BD] bg-white border-[#E2E2DF] cursor-not-allowed ';
          else cls += 'bg-white text-[#1A3C34] border-[#E2E2DF] hover:border-[#C77D63] hover:bg-[#F0EFEA] cursor-pointer ';
          return (
            <button
              key={idx}
              type="button"
              disabled={isPast}
              onClick={() => !isPast && !dimmed && onToggle(iso, dow)}
              className={cls}
              data-testid={`recurring-day-${iso}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
